import React, { useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';
import { convertPointsApi } from '@/api/vendo';

export interface ConvertPointsModalData {
   balance: number;
   pointsPerCredit: number;
}

interface Props {
   data: ConvertPointsModalData;
}

const ConvertPointsModal = ({ data }: Props) => {
   const { balance, pointsPerCredit } = data;
   const { handleClose } = useUIStore();
   const qc = useQueryClient();
   const max = Math.floor(balance / pointsPerCredit);
   const [credits, setCredits] = useState(max > 0 ? 1 : 0);

   const clamp = (n: number) => Math.max(0, Math.min(max, Math.floor(n) || 0));
   const cost = credits * pointsPerCredit;

   const convert = useMutation({
      mutationFn: () => convertPointsApi(credits),
      onSuccess: (r) => {
         toast.success(
            `Converted ${r.pointsSpent.toLocaleString()} pts → ${r.creditsGranted} credit${
               r.creditsGranted === 1 ? '' : 's'
            }`
         );
         qc.invalidateQueries({ queryKey: ['vendo', 'my-points'] });
         qc.invalidateQueries({ queryKey: ['vendo', 'credits'] });
         handleClose();
      },
      onError: (e: any) =>
         toast.error(e?.response?.data?.error || 'Conversion failed'),
   });

   const canConvert = credits > 0 && cost <= balance && !convert.isPending;

   return (
      <Wrapper>
         <Summary>
            <SumRow>
               <SumLbl>Your points</SumLbl>
               <SumVal>{balance.toLocaleString()}</SumVal>
            </SumRow>
            <SumHint>
               {pointsPerCredit.toLocaleString()} pts = 1 credit · up to{' '}
               <strong>{max.toLocaleString()}</strong> credit{max === 1 ? '' : 's'}
            </SumHint>
         </Summary>

         {max === 0 ? (
            <EmptyNote>
               You need at least {pointsPerCredit.toLocaleString()} points to convert
               1 credit. Keep earning! 🎁
            </EmptyNote>
         ) : (
            <>
               <FieldLabel>How many credits?</FieldLabel>
               <Stepper>
                  <StepBtn
                     type="button"
                     onClick={() => setCredits((c) => clamp(c - 1))}
                     disabled={credits <= 0}
                  >
                     −
                  </StepBtn>
                  <StepInput
                     type="number"
                     min={0}
                     max={max}
                     value={credits}
                     onChange={(e) => setCredits(clamp(Number(e.target.value)))}
                  />
                  <StepBtn
                     type="button"
                     onClick={() => setCredits((c) => clamp(c + 1))}
                     disabled={credits >= max}
                  >
                     +
                  </StepBtn>
                  <MaxBtn type="button" onClick={() => setCredits(max)}>
                     Max
                  </MaxBtn>
               </Stepper>

               <CostBox>
                  <CostRow>
                     <span>Points to spend</span>
                     <CostVal>−{cost.toLocaleString()}</CostVal>
                  </CostRow>
                  <CostRow>
                     <span>Credits you get</span>
                     <CostVal $green>+{credits.toLocaleString()}</CostVal>
                  </CostRow>
                  <CostRow>
                     <span>Points left after</span>
                     <span>{(balance - cost).toLocaleString()}</span>
                  </CostRow>
               </CostBox>

               <ConfirmNote>
                  This converts <strong>{cost.toLocaleString()} points</strong> into{' '}
                  <strong>{credits.toLocaleString()} credit{credits === 1 ? '' : 's'}</strong>
                  . This can't be undone.
               </ConfirmNote>
            </>
         )}

         <BtnRow>
            <div onClick={handleClose}>
               <Btn title="Cancel" type="outline" size="small" />
            </div>
            <div onClick={() => canConvert && convert.mutate()}>
               <Btn
                  title={convert.isPending ? 'Converting…' : `Convert ${credits} credit${credits === 1 ? '' : 's'}`}
                  type={canConvert ? 'normal' : 'disabled'}
                  size="small"
                  bgcolor={canConvert ? colors.PRIMARY : colors.GRAY10}
                  textcolor={canConvert ? colors.WHITE : colors.GRAY1}
               />
            </div>
         </BtnRow>
      </Wrapper>
   );
};

export default ConvertPointsModal;

const Wrapper = styled.div`
   width: 100%;
   min-width: 0;
   display: flex;
   flex-direction: column;
   gap: 14px;
`;

const Summary = styled.div`
   background: var(--app-brand-tint);
   border-radius: 12px;
   padding: 14px 16px;
`;

const SumRow = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;
`;

const SumLbl = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.GRAY3};
`;

const SumVal = styled.span`
   font-size: 24px;
   font-weight: 900;
   color: ${colors.PRIMARY};
`;

const SumHint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-top: 4px;
`;

const EmptyNote = styled.p`
   font-size: 13px;
   color: ${colors.GRAY3};
   text-align: center;
   padding: 8px 0;
`;

const FieldLabel = styled.label`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY1};
`;

const Stepper = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;

const StepBtn = styled.button`
   width: 38px;
   height: 38px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.WHITE};
   font-size: 20px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   cursor: pointer;
   &:disabled { opacity: 0.4; cursor: default; }
`;

const StepInput = styled.input`
   flex: 1;
   height: 38px;
   text-align: center;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   font-size: 16px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
   font-family: inherit;
   outline: none;
   &:focus { border-color: ${colors.PRIMARY}; }
`;

const MaxBtn = styled.button`
   height: 38px;
   padding: 0 12px;
   border-radius: 8px;
   border: 1px solid ${colors.PRIMARY};
   background: transparent;
   color: ${colors.PRIMARY};
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
`;

const CostBox = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   padding: 12px 14px;
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const CostRow = styled.div`
   display: flex;
   justify-content: space-between;
   font-size: 13px;
   color: ${colors.GRAY3};
`;

const CostVal = styled.span<{ $green?: boolean }>`
   font-weight: 800;
   color: ${({ $green }) => ($green ? colors.SUCCESS : colors.DANGER)};
`;

const ConfirmNote = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
`;
